import { isNavigationFailure, NavigationFailureType, useRoute, type NavigationFailure } from 'vue-router';

import { usePageReload } from '@/shared/lib/composables';

import type { MenuLink } from './menu-types';

import { isMenuLinkActive } from './route-branch';
import { useSidebar } from './use-sidebar';

/**
 * Навигация по пункту меню, общая для рейла, панели подразделов и мобильной шторки.
 */
export function useMenuNavigation() {
  const route = useRoute();
  const { closeMobileMenu } = useSidebar();
  const { reloadCurrentPage } = usePageReload();

  // Активен ли сейчас пункт меню; правило ветки живёт в model/route-branch.
  const isLinkActive = (item: MenuLink): boolean =>
    isMenuLinkActive(item, typeof route.name === 'string' ? route.name : '');

  const isLinkHighlighted = (item: MenuLink): boolean => route.meta.navigationSection === item.id || isLinkActive(item);

  /**
   * Клик по пункту меню-ссылке (RouterLink в режиме `custom` — навигацию инициируем сами
   * через `navigate`). На mobile закрывает шторку. Если пункт ведёт на уже активную
   * страницу, обычный переход был бы no-op («ничего не происходит») — вместо этого делаем
   * мягкую перезагрузку: `navigate` сбрасывает query к дефолту пункта, а после завершения
   * навигации пересоздаём компонент страницы (reloadCurrentPage). `navigate` сам отдаёт
   * ctrl/⌘/средний клик браузеру («открыть в новой вкладке») — такой клик не перезагружаем.
   *
   * Отменённый или прерванный переход страницу не пересоздаёт: guard страницы уже
   * сказал «остаться», и повторное монтирование потеряло бы черновик. Сама
   * перезагрузка тоже спрашивает guard'ы и может отказать.
   */
  const handleLinkClick = async (
    item: MenuLink,
    navigate: (event: MouseEvent) => Promise<void | NavigationFailure | undefined>,
    event: MouseEvent,
  ): Promise<void> => {
    closeMobileMenu();

    const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
    const shouldReload = !isModifiedClick && isLinkActive(item);

    const failure = await navigate(event);

    if (!shouldReload) {
      return;
    }

    if (
      isNavigationFailure(failure, NavigationFailureType.aborted) ||
      isNavigationFailure(failure, NavigationFailureType.cancelled)
    ) {
      return;
    }

    await reloadCurrentPage();
  };

  return { isLinkActive, isLinkHighlighted, handleLinkClick };
}
